import { useEffect, useState } from 'react';
import { labHostsAPI } from '../services/api';
import {
  PROGRAM_TYPES,
  USER_CATEGORIES,
  MODES,
  CENTERS,
  PROGRAM_NAMES,
  linuxGroupsForProgramType,
  linuxHomeForGroup,
} from '../domain/pma/pmaIdentityPolicy';
import {
  PMA_TOOLS,
  PMA_LINUX_GROUPS,
  PMA_VNC_FQDNS,
  parsePmaTools,
  togglePmaTool,
  linuxGroupsForCgHost,
} from '../domain/pma/pmaLabAccess';
import {
  DURATION_EDITABLE_PROGRAMS,
  PO_SCHEDULES,
  defaultDurationToken,
  formatPmaSheetDate,
  resolvePmaLifecycle,
} from '../domain/pma/pmaLifecyclePolicy';

export default function PmaIdentityPolicyForm({ policy, onChange }) {
  const [fqdns, setFqdns] = useState(null);
  useEffect(() => {
    let cancelled = false;
    labHostsAPI
      .list()
      .then((res) => {
        if (cancelled) return;
        setFqdns((res.data.hosts || []).filter((host) => host.isActive !== false).map((host) => host.fqdn));
      })
      .catch(() => {
        if (!cancelled) setFqdns(PMA_VNC_FQDNS);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const fqdnOptions = fqdns || [];
  const set = (patch) => onChange({ ...policy, ...patch });
  const selectedTools = parsePmaTools(policy.tools);
  const lifecycle = resolvePmaLifecycle(policy);
  const durationEditable = DURATION_EDITABLE_PROGRAMS.includes(policy.programType);

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold theme-text">ID generation policy</h2>
      <p className="text-sm theme-text-secondary">
        user-id follows spec 4.2 (e.g. <code>po_b27n_sujeet_547</code>). Student ID uses OF / ON
        (not O / N — N is Noida): <code>ON26TI0001</code>, <code>OFN26PO0001</code>. Generated for
        normal and bulk workshop only. Online drops the center letter.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="text-sm theme-text">
          program_type
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.programType}
            onChange={(e) => {
              const programType = e.target.value;
              set({
                programType,
                programName: PROGRAM_NAMES[programType] || policy.programName,
                programDuration: defaultDurationToken({
                  programType,
                  userCategory: policy.userCategory,
                  poSchedule: policy.poSchedule,
                }),
                linuxGroups: linuxGroupsForProgramType(programType, policy.linuxGroups),
              });
            }}
          >
            {PROGRAM_TYPES.map((p) => (
              <option key={p} value={p}>
                {p} — {PROGRAM_NAMES[p] || p}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm theme-text">
          program_name
          <input
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.programName}
            onChange={(e) => set({ programName: e.target.value })}
          />
        </label>
        <label className="text-sm theme-text">
          user_category
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.userCategory}
            onChange={(e) => set({ userCategory: e.target.value })}
          >
            {USER_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm theme-text">
          mode
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.mode}
            onChange={(e) => set({ mode: e.target.value })}
          >
            {MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm theme-text">
          Center {policy.mode === 'Online' ? '(ignored for Online)' : ''}
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.center}
            disabled={policy.mode === 'Online'}
            onChange={(e) => set({ center: e.target.value })}
          >
            {CENTERS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm theme-text">
          Batch_no
          <input
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            placeholder="e.g. 27"
            value={policy.batchNo}
            onChange={(e) => set({ batchNo: e.target.value })}
          />
        </label>
        {policy.programType === 'PO' ? (
          <label className="text-sm theme-text">
            PO schedule (spec 7.2)
            <select
              className="block w-full mt-1 px-2 py-2 rounded border theme-text"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
              value={policy.poSchedule || 'regular'}
              onChange={(e) => {
                const poSchedule = e.target.value;
                set({
                  poSchedule,
                  programDuration: defaultDurationToken({
                    programType: policy.programType,
                    userCategory: policy.userCategory,
                    poSchedule,
                  }),
                });
              }}
            >
              {PO_SCHEDULES.map((schedule) => (
                <option key={schedule} value={schedule}>
                  {schedule === 'weekend' ? 'Weekend — 15 months' : 'Regular — 9 months'}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {durationEditable ? (
          <label className="text-sm theme-text">
            program duration
            <input
              className="block w-full mt-1 px-2 py-2 rounded border theme-text"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
              placeholder="e.g. 15d or 9m"
              value={policy.programDuration || ''}
              onChange={(e) => set({ programDuration: e.target.value })}
            />
          </label>
        ) : null}
        <p className="text-sm theme-text-secondary sm:col-span-2 lg:col-span-3">
          {lifecycle.exempt
            ? lifecycle.reason
            : `${lifecycle.reason}. Expiry ${formatPmaSheetDate(lifecycle.expiryDate)} · delete ${formatPmaSheetDate(lifecycle.deleteDate)} · backup purge ${formatPmaSheetDate(lifecycle.purgeDate)}`}
        </p>
        <fieldset className="text-sm theme-text sm:col-span-2 lg:col-span-3">
          <legend className="mb-1">
            linux-group
            {(policy.linuxGroups || [])[0] ? (
              <span className="ml-2 font-normal theme-text-secondary">
                home {linuxHomeForGroup(policy.linuxGroups[0])}
              </span>
            ) : null}
          </legend>
          <div className="flex flex-wrap gap-3">
            {PMA_LINUX_GROUPS.map((group) => {
              const checked = (policy.linuxGroups || []).includes(group);
              return (
                <label key={group} className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = policy.linuxGroups || [];
                      const next = checked
                        ? current.filter((g) => g !== group)
                        : [...current, group];
                      set({
                        linuxGroups: PMA_LINUX_GROUPS.filter((g) => next.includes(g)),
                      });
                    }}
                  />
                  {group}
                </label>
              );
            })}
          </div>
        </fieldset>
        <fieldset className="text-sm theme-text sm:col-span-2 lg:col-span-3">
          <legend className="mb-1">tools</legend>
          <div className="flex flex-wrap gap-3">
            {PMA_TOOLS.map((tool) => {
              const checked = selectedTools.includes(tool);
              return (
                <label key={tool} className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => set({ tools: togglePmaTool(policy.tools, tool) })}
                  />
                  {tool}
                </label>
              );
            })}
          </div>
        </fieldset>
        <label className="text-sm theme-text">
          vnc-fqdn
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.vncFqdn}
            onChange={(e) => {
              const vncFqdn = e.target.value;
              set({
                vncFqdn,
                linuxGroups: PMA_LINUX_GROUPS.filter((g) =>
                  linuxGroupsForCgHost(policy.linuxGroups, vncFqdn).includes(g)
                ),
              });
            }}
          >
            <option value="">Select VNC FQDN</option>
            {fqdnOptions.map((fqdn) => (
              <option key={`vnc-${fqdn}`} value={fqdn}>
                {fqdn}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm theme-text">
          putty-fqdn
          <select
            className="block w-full mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={policy.puttyFqdn}
            onChange={(e) => {
              const puttyFqdn = e.target.value;
              set({
                puttyFqdn,
                linuxGroups: PMA_LINUX_GROUPS.filter((g) =>
                  linuxGroupsForCgHost(policy.linuxGroups, policy.vncFqdn || puttyFqdn).includes(g)
                ),
              });
            }}
          >
            <option value="">Select PuTTY FQDN</option>
            {fqdnOptions.map((fqdn) => (
              <option key={`putty-${fqdn}`} value={fqdn}>
                {fqdn}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
